import { ScrollArea } from "@radix-ui/themes";
import { t } from "i18next";
import _ from "lodash";
import { useEffect, useState } from "react";
import { TbX } from "react-icons/tb";
import { getPerfectDateTime } from "../../../../helpers/utils";
import { useFetch } from "../../../../hooks/useFetch";
import { Change } from "../../../../types/types";
import NoData from "../../../../ui/NoData/NoData";
import { SimpleDataTable } from "../../../../ui/DataTable/SimpleDataTable";
import { ColumnDef } from "@tanstack/react-table";
import CategoryPopover from "../../../../components/Filters/subFilterPopover";
import BooleanCategoryPopover from "../../../../components/Filters/subFilterBooleanPopover";
import { changesTypes } from "../../../../enums";

type Props = {
  id: string;
};

type ChangelogTableData = {
  id: string;
  date: string;
  type: string;
  action: string | number;
};

// Получение изменения по его типу
function getActionByType(change: Change) {
  switch (change.type) {
    case "equipment_item_division_changed":
      return change.attributes.division_name;
    case "equipment_item_position_changed":
      return change.attributes.position;
    case "equipment_item_parent_changed":
      return change.attributes.parent_name;
    case "equipment_item_responsible_changed":
      return change.attributes.responsible_name ? change.attributes.responsible_name : t("no_selected");
    case "equipment_item_status_changed":
      return t(change.attributes.status);
    case "equipment_item_move_status_changed":
      return change.attributes.move_status ? t("move_" + change.attributes.move_status) : t("no_selected");
    case "equipment_item_performer_changed":
      return change.attributes.performer_name ? change.attributes.performer_name : t("no_selected");
    case "sites_equipment_item_changes_watchdogs":
      return change.attributes.move_status ? t("move_" + change.attributes.move_status) : t("no_selected");
    case "sites_equipment_item_changes_trigger_rfid_ins":
    case "sites_equipment_item_changes_trigger_rfid_switches":
    case "sites_equipment_item_changes_trigger_rfid_out":
    case "sites_equipment_item_changes_trigger_value_less_thans":
    case "sites_equipment_item_changes_trigger_value_less_thans":
      return t("trigger_in_action");
    default:
      return change.type;
  }
}

const ChangeLogTab = ({ id }: Props) => {
  const [loading, setLoading] = useState(true)
  const [selectedDates, setSelectedDates] = useState({from: null, to: null})
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [params, setParams] = useState(new URLSearchParams({}));
  const [{ response, isLoading, error }, doFetchChanges] = useFetch(`/directories/equipment_items/changes/filter?${params.toString()}`);
  const [nodes, setNodes] = useState<{
    id: string,
    type: string,
    action: string | number,
    date: string
  }[]>([])

  useEffect(() => {
    setLoading(true)
    setParamsForChanges();
    doFetchChanges();
  }, []);

  useEffect(() => {
    setLoading(true)
    setParamsForChanges();
    doFetchChanges();
  }, [selectedTypes]);

  function setParamsForChanges() {
    let newParams = new URLSearchParams({});
    newParams.append("equipment_item_ids[]", id)
    selectedTypes.map(st => {
      newParams.append("types[]", st)
    })
    if(selectedDates.from && selectedDates.to) {
      newParams.append("from", selectedDates.from)
      newParams.append("to", selectedDates.to)
    }
    setParams(newParams)
  }

  useEffect(() => {
    if(!response?.data) { return }
    let changes = response?.data as unknown as Change[];
    let nodes = changes?.map((change) => {
      return {
        id: change.id,
        date: getPerfectDateTime(change.attributes.at),
        type: change.type,
        action: getActionByType(change),
      };
    });
    setNodes(_.sortBy(nodes, []))
  }, [response]);


  const columns: ColumnDef<ChangelogTableData>[] = [
    {
      id: 'id',
      header: `${t("type")}`,
      size: 1000,
      cell: (props) => t(`${props.row.original.type}`),
    },

    {
      accessorKey: "action",
      header: t("event_result"),
      size: 200,
      minSize: 200,
      maxSize: 200,
      cell: (props) => t(`${props.row.getValue("action")}`),
    },
    {

      header: t("date"),
      size: 150,
      minSize: 150,
      maxSize: 150,
      cell: (props) => (
        <div className="max-w-[420px]">
          <div className="font-medium truncate ">{props.row.original.date}</div>
        </div>
      ),
    },
  ];

  return (
    <div className="relative flex flex-col w-full h-full overflow-hidden bg-background">
      <div className="flex flex-none w-full h-24 text-base gap-2 items-center justify-between px-6">
        <div className="flex flex-col space-y-1">
          <span className="text-2xl font-semibold">{t('equipment_changelog')}</span>
        </div>
      </div>
      <div className="flex gap-2 px-6 mb-2">
        <CategoryPopover
          categoryName={t("type")}
          content={changesTypes.map(ct => ct.value)}
          filterState={[selectedTypes, setSelectedTypes]}
          convertFromRuby={true}
        />
        {/* <CategoryPopover
          categoryName={t("equipment_type_name")}
          content={equipment_types}
          filterState={[typesFilter, setTypesFilter]}
        />
        <CategoryPopover
          categoryName={t("division")}
          content={divisions}
          filterState={[divisionsFilter, setDivisionsFilter]}
        />
        <CategoryPopover
          categoryName={t("status")}
          content={equipmentStatuses}
          filterState={[statusesFilter, setStatusesFilter]}
          searchbar={false}
          fixed={true}
        />
        <BooleanCategoryPopover // Отдельный фильтр с radiogroup элементами
          categoryName={t("with_sensors")}
          content={sensors}
          filterState={[sensorsFilter, setSensorsFilter]}
        /> */}
      </div>
      {!loading && !nodes?.length ? ( // Заглушка для отсутствия данных при поиске
        <div className="flex w-full h-full justify-center items-center">
            {t("no_data")}
        </div>
        ) : (
          <SimpleDataTable data={nodes} columns={columns}/>
        )}
    </div>
  );
};

export default ChangeLogTab;
